import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import CorgeeStorageHelper from '@components/Globals/CorgeeStorageHelper';
import * as CorgeeHelper from '@lib/corgeeHelper';
import { addTargetBlankToLinks } from '@lib/linkUtils';
import { handleOriginalConversationLinkClick, handleLinkedCardClick } from '@/lib/urlUtils';
import SummernoteCorgee from '@components/Globals/SummernoteCorgee';
import type { SummernoteCorgeeRef } from '@components/Globals/SummernoteCorgee';
import CustomModal from '@components/Globals/CustomModal';
import FileUpload from '@components/Globals/FileUpload';
import CardConversationLayers from '@components/Globals/CardConversationLayers';
import type { CardConversationLayersRef } from '@components/Globals/CardConversationLayers';
import AttachmentsContent from './AttachmentsContent';
import ActionMenuReply from './ActionMenuReply';
import ReplyApi from './api/ReplyApi';
import { ImagesGallery, type Attachment as GalleryAttachment } from '@components/ImagesGallery';
import ConversationApi from './api/ConversationApi';
import ProfilePictureModal from './ProfilePictureModal';
import CorgeeModalStats from '@components/Globals/CorgeeModalStats';
import '../../assets/fonts/glyphs/material-icons.css';
import $ from 'jquery';
import AuthService from './api/AuthServices';
import { FacebookSelector, FacebookCounter } from '../Reactions/Reactions';
import ClickOutSide from '@components/Globals/ClickOutSide';
import './SingleReply.css';
import Select from 'react-select';
import { buildChatWindowUrl } from '@lib/chatUrl';
import { normalizeConversationId } from '@lib/conversationUrl';
//import threeDotsSvg from '@/assets/icons/three-dots.svg';
//import 'react-select/dist/react-select.css';

// Types
interface Attachment {
  // Define attachment properties
  [key: string]: any;
}

interface ReplyLayer {
  LayerId: number;
  LayerColor?: string;
  Color?: string;
  PriorityInConfigValue: number;
}

interface Reaction {
  ReplyID: number;
  conversationid: number;
  personid: number;
  by: string;
  emoji: string;
  ImageName: string;
}

interface Reply {
  ReplyID: number;
  ReplyId: number;
  Body: string;
  IsImportant: boolean;
  CreatedBy: string;
  CreatedById: number;
  CreatedAt: string;
  UpdatedAt?: string;
  UpdatedBy?: string;
  ImageName: string;
  Attachments: Attachment[];
  ReplyLayers?: ReplyLayer[];
  IsEnable: boolean;
  IsDetailUpdate?: boolean;
  reactions: Reaction[];
  reactSelfUpdate?: boolean;
  ConversationId: number;
  ConversationPhaseId?: number;
  PhaseName?: string;
  UpdateReaction?: boolean;
}

interface Conversation {
  ConversationId: number;
  Highlight: boolean;
  ProjectId: number;
  ProjectName: string;
  UpdatedAt: string;
  ConfigValueId: number;
  HasPhases?: boolean;
  Phases?: Phase[];
}

interface Phase {
  ConversationPhaseId: number;
  Title: string;
}

interface PhaseOption {
  value: number;
  label: string;
}

interface ApiConfig {
  url: string;
  [key: string]: any;
}

interface Config {
  webChatUrl?: string;
  weburl?: string;
  [key: string]: any;
}

interface ModalState {
  open: boolean;
  title: string;
  template: React.ReactNode[] | null;
}

interface EmbeddedImage {
  AttachmentId: number;
  EmbeddedImageId: number;
  FileUrl: string;
  FileName: string;
  FileExtension?: string;
  Description?: string;
  Title?: string;
  Favorite?: number;
  Thumbnail: {
    FileUrl: string;
    FileName?: string;
  };
}

interface CorgeeFunctions {
  openInfoWindow?: (conversationId: string) => void;
  openChat?: (personId: number) => void;
  onUpdateReply?: (conversation: Conversation, reply: Reply, updates: any, param: number) => void;
  onUpdateReplyReactions?: (reply: Reply) => void;
  loadingBarStart?: () => void;
  loadingBarComplete?: () => void;
  onHoverProfilePicture?: (event: React.MouseEvent, personId: number) => void;
  onLeaveProfilePicture?: () => void;
}

interface SingleReplyProps {
  conversationId: number;
  reply: Reply;
  conversation: Conversation;
  api: ApiConfig;
  config: Config;
  onUpdateReply?: (response: any, replyId: number) => void;
  onDeleteReply?: (replyId: number) => void;
  onAddRepost?: (reply: Reply) => void;
  onSubmitTime?: (conversation?: any) => void;
  onAddReply?: (
    conversationId: number,
    reply: any,
    sendUpdate?: boolean,
    updateTotalReplies?: boolean
  ) => void;
  hideModals?: boolean;
  isConversationDetailsView?: boolean;
  repliesPendingToHighlight?: number[];
  removePendingHighlight?: (replyId: number) => void;
  SocketConnectionId?: string;
  configMenus?: any;
  isInfowindowViewCard?: boolean;
  corgeeFunctions?: CorgeeFunctions;
  storageData?: {
    enableChat?: boolean;
    personid?: number;
  };
  chatData?: {
    enableChat?: boolean;
    personid?: number;
  };
  phaseSelected?: Phase;
  openNewWindow?: (url: string) => void;
  isHomeView?: boolean;
  onAddLayerToCard?: (...args: any[]) => void;
  onAddLayerToProject?: (...args: any[]) => void;
  onSetFunctionsCardLayers?: (...args: any[]) => void;
}

let windowReferenceSafari: Window | null = null;

// Must match the .edit-reply-modal transition in SingleReply.css
const EDIT_REPLY_MODAL_EXIT_MS = 200;

/* Matches the truncated body max-height in ConversationViewCard.css */
const SEE_MORE_MAX_HEIGHT = 137;

// Reduces reply body HTML to just its visible text plus image sources, so the "Draft will
// be missed" confirmation compares what the user would actually notice changed rather than
// incidental HTML differences (attribute order, whitespace, wrapper tags Summernote adds).
// innerHTML is used rather than $(): jQuery only treats a string as HTML when it starts with
// '<', so a body stored as plain text would be handed to Sizzle as a selector and throw.
const normalizeReplyBodyForCompare = (html?: string | null): string =>
{
  const parser = document.createElement('div');
  parser.innerHTML = html ?? '';
  const text = (parser.textContent ?? '').replace(/\s+/g, ' ').trim();
  const imageSrcs = Array.from(parser.querySelectorAll('img'))
    .map(img => img.getAttribute('src') ?? '')
    .sort()
    .join('|');
  return `${text}::${imageSrcs}`;
};

const SingleReply: React.FC<SingleReplyProps> = ({
  conversationId,
  reply: initialReply,
  conversation,
  api,
  config,
  onUpdateReply,
  onDeleteReply,
  onAddRepost,
  onSubmitTime,
  onAddReply,
  hideModals,
  isConversationDetailsView,
  repliesPendingToHighlight = [],
  removePendingHighlight,
  SocketConnectionId,
  configMenus,
  isInfowindowViewCard,
  corgeeFunctions,
  storageData,
  chatData,
  phaseSelected,
  openNewWindow,
  isHomeView = false,
  onAddLayerToCard,
  onAddLayerToProject,
  onSetFunctionsCardLayers,
}) =>
{
  // Refs
  const singleReplyElementRef = useRef<HTMLDivElement>(null);
  const profilePictureItemRef = useRef<any>(null);

  // State
  const [me] = useState(AuthService.PersonId());
  const [name] = useState(AuthService.GivenName());
  const [imageProfile] = useState(AuthService.ImageProfile());
  const [showSelector, setShowSelector] = useState(false);
  const [selectorPos, setSelectorPos] = useState({ right: 0, bottom: 0 });
  const reactionTriggerRef = useRef<HTMLSpanElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevIsImportantRef = useRef(initialReply.IsImportant);
  const isSavingRef = useRef(false);
  const layersWidgetRef = useRef<CardConversationLayersRef>(null);
  // Refreshes the backend-sourced tag list (getPeopleTagList) after a layer is
  // added mid-edit - layers/editLayersSelected updating alone only makes the
  // client-side filtering re-run against the same stale people list; the
  // actual list of who's now taggable still needs a fresh backend fetch.
  const summernoteEditModalRef = useRef<SummernoteCorgeeRef>(null);
  // True once the user interacts with the layers widget in the Edit Reply modal
  const layersTouchedRef = useRef(false);
  const pendingUpdatedAtRef = useRef<string | null>(null);
  const [enableEditBody, setEnableEditBody] = useState(false);
  // Editor opened from the action menu: shown in a modal instead of inline
  const [editInModal, setEditInModal] = useState(false);
  // Keeps the modal mounted during its fade-out
  const [editModalExiting, setEditModalExiting] = useState(false);
  const editModalExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [editReplyDragPos, setEditReplyDragPos] = useState<{ x: number; y: number } | null>(null);
  const editReplyModalRef = useRef<HTMLDivElement>(null);
  const editReplyDragHandlersRef = useRef<{ onMove: (e: MouseEvent) => void; onUp: () => void } | null>(null);
  // Attachments and layers being edited in the Edit Reply modal
  const [editAttachments, setEditAttachments] = useState<Attachment[]>([]);
  const [editLayersSelected, setEditLayersSelected] = useState<ReplyLayer[]>([]);
  // Snapshot of the draft's starting point, captured when the editor opens (startEditBody).
  // Closing compares the live draft against these to decide whether anything was actually
  // changed, so the "Draft will be missed" confirmation only appears when it's true — not
  // just because the reply being edited happens to already have a body/attachments.
  const originalEditBodyRef = useRef<string>(initialReply.Body ?? '');
  const originalEditAttachmentIdsRef = useRef<string[]>([]);
  const originalEditLayerIdsRef = useRef<number[]>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [showLayerIsRequired, setShowLayerIsRequired] = useState(false);
  const [displayFullContent, setDisplayFullContent] = useState(
    !isInfowindowViewCard && (isConversationDetailsView ?? false),
  );
  const [showSeeMoreLink, setShowSeeMoreLink] = useState(false);
  const [reply, setReply] = useState<Reply>(initialReply);
  const summernoteRef = useRef<any>(null);
  const [body, setBody] = useState(initialReply.Body);
  const [highlight, setHighlight] = useState(conversation.Highlight);
  const [bodyEdited, setBodyEdited] = useState(initialReply.Body);
  const [showBodyIsRequired, setShowBodyIsRequired] = useState(false);
  const [highlightReply, setHighlightReply] = useState(false);
  const [embeddedImages, setEmbeddedImages] = useState<EmbeddedImage[]>([]);
  const [selectedImageId, setSelectedImageId] = useState(0);
  const [openGallery, setOpenGallery] = useState(false);
  const [canOpenChat, setCanOpenChat] = useState(false);
  const [reqNewChat, setReqNewChat] = useState<any>({});
  const [firstLayerColor, setFirstLayerColor] = useState(
    initialReply.ReplyLayers && initialReply.ReplyLayers.length > 0
      ? (initialReply.ReplyLayers[0]?.LayerColor ?? initialReply.ReplyLayers[0]?.Color)
      : '',
  );
  const [replyLayers, setReplyLayers] = useState(initialReply.ReplyLayers);
  const [openProfilePictureDialog, setOpenProfilePictureDialog] = useState(false);
  const [eventHover, setEventHover] = useState<any>({});
  const [positionProfilePictureY, setPositionProfilePictureY] = useState(0);
  const [positionProfilePictureX, setPositionProfilePictureX] = useState(0);
  const [modal, setModal] = useState<ModalState>({
    open: false,
    title: '',
    template: null,
  });
  const [_showReaction, setShowReaction] = useState(true);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [comboPhaseSelected, setComboPhaseSelected] = useState(0);
  const [replyPhases, setReplyPhases] = useState<PhaseOption[]>([]);
  const [isImportant, setIsImportant] = useState(initialReply.IsImportant);

  // Highlight replies
  const highlightReplies = useCallback(() =>
  {
    // The flash exists to draw attention to activity from other people.
    // Adding your own reply shouldn't highlight it. Checked here rather than
    // at each call site so every trigger path is covered.
    if (initialReply.CreatedById === CorgeeStorageHelper.personId())
    {
      return;
    }

    setHighlightReply(true);
    setTimeout(() =>
    {
      setHighlightReply(false);
    }, 2000);
  }, [initialReply.CreatedById]);

  // Measures the real content height, which stays readable while collapsed
  const measureSeeMore = useCallback(() =>
  {
    if (!singleReplyElementRef.current) return;

    const listener = singleReplyElementRef.current.querySelector<HTMLElement>(
      '.corgee_com_card-body-description-container > .card-content-container-height-listener',
    );
    const content = listener?.firstElementChild as HTMLElement | null;
    if (!listener || !content) return;

    const height = Math.max(content.offsetHeight, content.scrollHeight, listener.scrollHeight);
    setShowSeeMoreLink(height > SEE_MORE_MAX_HEIGHT);
  }, []);

  // Count images
  const countImages = useCallback(
    (html: HTMLElement) =>
    {
      // Images already in cache never fire load, so only pending ones are awaited
      const images = Array.from(
        html.querySelectorAll<HTMLImageElement>('.card-content-container-height-listener img'),
      );
      const pending = images.filter(img => !img.complete);

      if (pending.length === 0)
      {
        measureSeeMore();
      }
      else
      {
        let settled = 0;
        const onSettled = () =>
        {
          settled++;
          if (settled === pending.length) measureSeeMore();
        };

        pending.forEach(img =>
        {
          img.addEventListener('load', onSettled, { once: true });
          img.addEventListener('error', onSettled, { once: true });
        });
      }

      $(html)
        .find('.card-content-container-height-listener')
        .bind('contextmenu', event =>
        {
          event.preventDefault();
        });
      $(html)
        .find('.attachments-img')
        .bind('contextmenu', event =>
        {
          event.preventDefault();
        });
    },
    [measureSeeMore],
  );

  // Handle embedded images click
  const handleEmbeddedImagesClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) =>
    {
      if (handleLinkedCardClick(e, id =>
        corgeeFunctions?.openInfoWindow?.(id as any),
      )) return;

      if (handleOriginalConversationLinkClick(e, id =>
        corgeeFunctions?.openInfoWindow?.({ ConversationId: id } as any),
      )) return;

      const target = e.target as HTMLElement;
      const imgElement = target.tagName === 'IMG' ? (target as HTMLImageElement) : null;

      if (imgElement)
      {
        e.preventDefault();
        const source = imgElement.src;
        ConversationApi.getEmbeddedImages(api as any, {
          id: reply.ReplyId,
          IsReply: true,
        }).then((response: EmbeddedImage[]) =>
        {
          let selectedId = 0;

          for (let i = 0; i < response.length; i++)
          {
            const element = response[i];
            if (!element) continue;
            if (element.Thumbnail.FileUrl === source || element.FileUrl === source)
            {
              selectedId = element.EmbeddedImageId;
              break;
            }
          }

          setOpenGallery(true);
          setEmbeddedImages(response);
          setSelectedImageId(selectedId);
        });
      }
    },
    [api, reply.ReplyId],
  );

  // Close gallery
  const onCloseGallery = useCallback(() =>
  {
    setEmbeddedImages([]);
    setOpenGallery(false);
    setSelectedImageId(0);
  }, []);

  // Get tags in body
  const getTagsInBody = useCallback(
    (bodyTags: string) =>
    {
      const listTags: any[] = [];
      try
      {
        const taggedPeople = $('<div>' + bodyTags + '</div>').find('span[person-id]');
        for (let i = 0; i < taggedPeople.length; i++)
        {
          const tagEl = taggedPeople[i];
          if (!tagEl) continue;
          listTags.push({
            PersonId: parseInt($(tagEl as any).attr('person-id')!),
            ConversationId: conversationId,
            ReplyId: reply.ReplyId,
            ReplyFollowing: 0,
          });
        }
        return listTags;
      }
      catch (err)
      {
        console.warn(err);
        return [];
      }
    },
    [conversationId, reply.ReplyId],
  );

  // Disable checklist
  const disableChecklist = useCallback((content: string) =>
  {
    const div = document.createElement('div');
    div.innerHTML = content;
    const checkboxes = $(div).find('.note-editor-checkbox');

    for (let ci = 0; ci < checkboxes.length; ci++)
    {
      $(checkboxes[ci] as any).attr('disabled', 'true');
    }

    content = div.innerHTML;

    try
    {
      $(content);
    }
    catch (err)
    {
      content = content.replace('<p><br></p>', '');
      content = '<p>' + content + '</p>';
    }

    return content;
  }, []);

  // Saved attachments shaped as FileUpload items
  const initialAttachmentFiles = useMemo(() =>
  {
    return (reply.Attachments ?? []).map(attachment => ({
      ...attachment,
      Description: attachment.Description ?? attachment.FileName,
      FileSize: attachment.FileSize ?? 0,
      Progress: 100,
      AllowedSize: true,
    }));
  }, [reply.Attachments]);

  // Start editing the reply body, inline or in a modal
  const startEditBody = useCallback(
    (e: React.MouseEvent, openInModal: boolean) =>
    {
      e.preventDefault();
      const items: PhaseOption[] = [];
      let conversationPhaseDefault = 0;
      const canEditAction = CorgeeHelper.convHasEditPermissions(null, reply);

      const hasThreeDotsAnimation = document.querySelector('.appendMovingDots');
      if (hasThreeDotsAnimation) return;

      if (!reply.IsDetailUpdate && canEditAction)
      {
        if (!enableEditBody && (openInModal || singleReplyElementRef.current))
        {
          if (conversation.HasPhases)
          {
            if (conversation.Phases && conversation.Phases.length > 0)
            {
              items.push({ value: 0, label: 'All Phases' });

              for (let index = 0; index < conversation.Phases.length; index++)
              {
                const phase = conversation.Phases[index];
                if (!phase) continue;
                items.push({
                  value: phase.ConversationPhaseId,
                  label: phase.Title,
                });
              }
            }
            conversationPhaseDefault = reply.ConversationPhaseId || 0;
          }
          setBodyEdited(body);
          originalEditBodyRef.current = body ?? '';

          if (openInModal)
          {
            // Cancel a pending fade-out, or the modal reopens hidden
            if (editModalExitTimerRef.current)
            {
              clearTimeout(editModalExitTimerRef.current);
              editModalExitTimerRef.current = null;
            }
            setEditModalExiting(false);
            setEditAttachments(initialAttachmentFiles);
            setEditLayersSelected((reply.ReplyLayers ?? []).filter(l => l.LayerId !== 0));
            setIsUploadingFile(false);
            setShowLayerIsRequired(false);
            layersTouchedRef.current = false;

            // Snapshot what "unchanged" means for this session of editing
            originalEditAttachmentIdsRef.current = initialAttachmentFiles
              .map(a => String((a as any).AttachmentId ?? (a as any).FileName ?? ''))
              .sort();
            originalEditLayerIdsRef.current = (reply.ReplyLayers ?? [])
              .filter(l => l.LayerId !== 0)
              .map(l => l.LayerId)
              .sort((a, b) => a - b);
          }

          setEditInModal(openInModal);
          setEnableEditBody(true);
          setReplyPhases(items);
          setComboPhaseSelected(conversationPhaseDefault);
        }
      }
    },
    [conversation, reply, enableEditBody, body, initialAttachmentFiles],
  );

  // On edit body (inline, triggered by double click)
  const onEditBody = useCallback(
    (e: React.MouseEvent) =>
    {
      startEditBody(e, false);
    },
    [startEditBody],
  );

  // On edit reply (modal, triggered from the reply action menu)
  const onEditBodyInModal = useCallback(
    (e: React.MouseEvent) =>
    {
      startEditBody(e, true);
    },
    [startEditBody],
  );

  // On see more content
  const onSeeMoreContent = useCallback((e: React.MouseEvent) =>
  {
    e.preventDefault();
    setDisplayFullContent(prev => !prev);
  }, []);

  // On change summernote
  const onChangeSummernote = useCallback((content: string) =>
  {
    /*
     * Parsed with innerHTML rather than $(content). jQuery only treats a string as HTML when
     * it starts with '<'; anything else goes to Sizzle as a selector. Editing a reply that has
     * a file but no text produces content like 'dw<span></span>' — no leading tag — so every
     * keystroke threw "Syntax error, unrecognized expression" before setBodyEdited could run,
     * leaving bodyEdited empty and blocking Update with "Enter a valid message".
     */
    const parser = document.createElement('div');
    parser.innerHTML = content ?? '';

    const firstNode = parser.firstChild;
    const isWrappedInDiv =
      firstNode?.nodeType === Node.ELEMENT_NODE &&
      (firstNode as HTMLElement).tagName === 'DIV';

    setBodyEdited(isWrappedInDiv ? content : '<div>' + content + '</div>');
  }, []);

  // Plays the fade-out, then unmounts the modal
  const startEditModalExit = useCallback(() =>
  {
    setEditModalExiting(true);

    if (editModalExitTimerRef.current)
    {
      clearTimeout(editModalExitTimerRef.current);
    }

    editModalExitTimerRef.current = setTimeout(() =>
    {
      editModalExitTimerRef.current = null;
      setEditModalExiting(false);
    }, EDIT_REPLY_MODAL_EXIT_MS);
  }, []);

  // Close editor
  const closeEditor = useCallback(
    (deleteDraft?: boolean) =>
    {
      if (deleteDraft === true)
      {
        if (editInModal)
        {
          startEditModalExit();
        }
        setEnableEditBody(false);
        setEditInModal(false);
        setShowBodyIsRequired(false);
        setShowConfirmationModal(false);

        setTimeout(() =>
        {
          updateLinkConversationsByView();
        }, 200);
      }
      else if (deleteDraft === false)
      {
        setShowConfirmationModal(false);
      }
      else
      {
        // Show the "Draft will be missed" confirmation only when the draft actually differs
        // from what editing started with — not just because the reply already had a body
        // (which is true for essentially every existing reply, modal or inline). Applies the
        // same way to X, Cancel and Esc, since they all funnel through this branch.
        const liveBody = summernoteRef.current?.getContent?.();
        const currentBody = (typeof liveBody === 'string' ? liveBody : bodyEdited) ?? '';
        const bodyChanged =
          normalizeReplyBodyForCompare(currentBody) !==
          normalizeReplyBodyForCompare(originalEditBodyRef.current);

        let attachmentsChanged = false;
        let layersChanged = false;
        if (editInModal)
        {
          const currentAttachmentIds = editAttachments
            .map(a => String(a.AttachmentId ?? a.FileName ?? ''))
            .sort();
          attachmentsChanged =
            JSON.stringify(currentAttachmentIds) !==
            JSON.stringify(originalEditAttachmentIdsRef.current);

          const currentLayerIds = editLayersSelected
            .map(l => l.LayerId)
            .sort((a, b) => a - b);
          layersChanged =
            JSON.stringify(currentLayerIds) !== JSON.stringify(originalEditLayerIdsRef.current);
        }

        const hasUnsavedChanges = bodyChanged || attachmentsChanged || layersChanged;

        if (hasUnsavedChanges)
        {
          const existingStyles = $('.ui-dialog.react-draggable').attr('style');
          if (existingStyles && !existingStyles.includes('z-index:8'))
          {
            $('.ui-dialog.react-draggable').attr('style', existingStyles + 'z-index:8 !important');
          }
          setShowConfirmationModal(true);
        }
        else
        {
          closeEditor(true);
        }
      }
    },
    [editInModal, editAttachments, editLayersSelected, bodyEdited],
  );

  // On key down editor (Esc closes it)
  // closeEditor must be a dep: it captures editInModal, and with an empty dep array this
  // callback kept a stale reference from mount (editInModal=false), so Esc always showed the
  // "Draft will be missed" confirmation even when closing the Edit Reply modal — unlike the
  // X/Cancel buttons, which call the up-to-date closeEditor and correctly skip it there.
  const onKeyDownEditor = useCallback((e: KeyboardEvent) =>
  {
    if (e.keyCode === 27)
    {
      closeEditor();
    }
  }, [closeEditor]);

  // Esc for the Edit Reply modal, attached directly to document instead of relying on
  // Summernote's onKeyDown plumbing (SummernoteCorgee's handleKeyDown defers it via a 250ms
  // setTimeout, and it only fires while focus is inside the editable body). That indirection
  // was swallowing the close here, and it never fired at all when focus was elsewhere in the
  // modal (layers widget, attachments, phase select). This listener only runs while the modal
  // is actually open, so it does not affect the inline editor's own Esc handling.
  useEffect(() =>
  {
    if (!(enableEditBody && editInModal)) return;

    const handleDocumentKeyDown = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape' || e.keyCode === 27)
      {
        closeEditor();
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [enableEditBody, editInModal, closeEditor]);

  // On change file upload (Edit Reply modal)
  const onChangeFileUpload = useCallback((filesUploaded: Attachment[] | boolean) =>
  {
    let showWarning = filesUploaded === true;

    if (Array.isArray(filesUploaded))
    {
      const uploadProgress = filesUploaded.map(x => x.Progress).find(x => x && x < 100);
      showWarning = uploadProgress !== undefined;
    }

    if (showWarning)
    {
      setIsUploadingFile(true);
    }
    else
    {
      setEditAttachments(Array.isArray(filesUploaded) ? filesUploaded : []);
      setIsUploadingFile(false);
    }
  }, []);

  // On remove attachment (Edit Reply modal)
  const onRemoveAttachment = useCallback((index: number) =>
  {
    setEditAttachments(prev =>
    {
      const copyAttachments = [...prev];
      copyAttachments.splice(index, 1);
      return copyAttachments;
    });
  }, []);

  // Restores the reply's own layers over the widget's default pick
  const applyReplyLayers = useCallback(() =>
  {
    const currentReplyLayers = (reply.ReplyLayers ?? []).filter(l => l.LayerId !== 0);
    if (currentReplyLayers.length === 0) return false;

    layersWidgetRef.current?.onSelectLayer(currentReplyLayers as any);
    layersWidgetRef.current?.onUpdateOriginalSelectedIds(currentReplyLayers as any);
    setEditLayersSelected(currentReplyLayers);

    const firstReplyLayer = currentReplyLayers[0];
    setFirstLayerColor(firstReplyLayer?.LayerColor || firstReplyLayer?.Color || '');
    return true;
  }, [reply.ReplyLayers]);

  // On change layers (Edit Reply modal)
  const onChangeEditLayers = useCallback(
    (itemsSelected: any) =>
    {
      // Before any user interaction the widget only reports its default pick
      if (!layersTouchedRef.current)
      {
        if (applyReplyLayers()) return;
      }

      const layersList: ReplyLayer[] = Array.isArray(itemsSelected)
        ? itemsSelected
        : (itemsSelected?.ConversationLayers ?? []);

      setEditLayersSelected(layersList);
      setShowLayerIsRequired(layersList.length === 0);

      // Color follows the first selected layer
      const firstLayer = layersList[0];
      setFirstLayerColor(firstLayer?.LayerColor || firstLayer?.Color || '');
    },
    [applyReplyLayers],
  );

  // Refreshes who's actually taggable (backend call) once editLayersSelected
  // has genuinely changed and re-rendered down into SummernoteCorgee's own
  // layers prop - calling getPeopleTagList() synchronously inside
  // onChangeEditLayers above would race React's batched update and still
  // read the stale layers value, since the prop hasn't propagated down yet.
  const editLayersSelectedIdsKey = editLayersSelected.map(l => l.LayerId).join(',');
  useEffect(() =>
  {
    summernoteEditModalRef.current?.getPeopleTagList();
  }, [editLayersSelectedIdsKey]);

  // On cancel edit body
  const onCancelEditBody = useCallback(() =>
  {
    closeEditor();
  }, [closeEditor]);

  useEffect(() =>
  {
    if (!(enableEditBody && editInModal))
    {
      // Resetting the position mid fade-out makes the modal jump
      if (!editModalExiting)
      {
        setEditReplyDragPos(null);
      }
      return;
    }
    requestAnimationFrame(() =>
    {
      const el = editReplyModalRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setEditReplyDragPos({
        x: (window.innerWidth - rect.width) / 2,
        y: Math.max(24, (window.innerHeight - rect.height) / 2),
      });
    });
  }, [enableEditBody, editInModal, editModalExiting]);

  useEffect(() =>
  {
    return () =>
    {
      if (editReplyDragHandlersRef.current)
      {
        document.removeEventListener('mousemove', editReplyDragHandlersRef.current.onMove);
        document.removeEventListener('mouseup', editReplyDragHandlersRef.current.onUp);
        editReplyDragHandlersRef.current = null;
      }
      if (editModalExitTimerRef.current)
      {
        clearTimeout(editModalExitTimerRef.current);
        editModalExitTimerRef.current = null;
      }
      document.body.classList.remove('corgee-modal-dragging');
    };
  }, []);

  const handleEditReplyDragMouseDown = useCallback((e: React.MouseEvent<HTMLElement>) =>
  {
    if (!editReplyDragPos) return;
    if ((e.target as HTMLElement).closest('.close-control')) return;
    e.preventDefault();

    const originX = editReplyDragPos.x;
    const originY = editReplyDragPos.y;
    const startX = e.clientX;
    const startY = e.clientY;

    const onMove = (ev: MouseEvent): void =>
    {
      const el = editReplyModalRef.current;
      const newX = originX + (ev.clientX - startX);
      const newY = originY + (ev.clientY - startY);
      setEditReplyDragPos({
        x: Math.max(0, Math.min(newX, window.innerWidth - (el?.offsetWidth ?? 0))),
        y: Math.max(0, Math.min(newY, window.innerHeight - (el?.offsetHeight ?? 0))),
      });
    };

    const onUp = (): void =>
    {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.classList.remove('corgee-modal-dragging');
      editReplyDragHandlersRef.current = null;
    };

    editReplyDragHandlersRef.current = { onMove, onUp };
    document.body.classList.add('corgee-modal-dragging');
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [editReplyDragPos]);

  // On save edit body
  const onSaveEditBody = useCallback(
    (e: React.MouseEvent) =>
    {
      e.preventDefault();

      // A reply must always keep at least one layer
      if (editInModal && editLayersSelected.filter(l => l.LayerId !== 0).length === 0)
      {
        setShowLayerIsRequired(true);
        return;
      }

      // The editor propagates its content debounced; read it directly on save.
      const liveBody = summernoteRef.current?.getContent?.();
      const newBody = (typeof liveBody === 'string' ? liveBody : bodyEdited).replace('&amp;', '&');
      const htmlBody = $('<div>' + newBody + '</div>').text();
      const imageTags = $('<div>' + newBody + '</div>').find('img');

      if ((htmlBody === '' || htmlBody.trim().length === 0) && imageTags.length === 0)
      {
        setShowBodyIsRequired(true);
      }
      else
      {
        const requestParams: any = { ...reply };
        requestParams.Body = newBody;

        // The modal also edits attachments and layers
        if (editInModal)
        {
          requestParams.Attachments = editAttachments;
          requestParams.ReplyLayers = editLayersSelected.map(layerItem => ({
            LayerId: layerItem.LayerId,
            LayerColor: layerItem.LayerColor ?? layerItem.Color ?? '',
            LayerName: (layerItem as any).LayerName ?? (layerItem as any).Name ?? '',
            ReplyId: reply.ReplyId,
            PriorityInConfigValue: layerItem.PriorityInConfigValue ?? 0,
          }));
        }

        requestParams.Tags = getTagsInBody(newBody);
        requestParams.SocketConnectionId = SocketConnectionId;

        requestParams.Body = disableChecklist(requestParams.Body);

        if (requestParams.Body === '<p><br></p>')
        {
          requestParams.Body = '<span></span>';
        }

        requestParams.CopyBody = requestParams.Body;
        requestParams.Body = requestParams.Body.replace(/<img[^>]*>/g, '');

        const divhtml = document.createElement('div');
        divhtml.innerHTML = requestParams.Body;
        $(divhtml).addClass('appendMovingDots');
        requestParams.Body = divhtml.outerHTML;

        // Show clean body immediately without waiting for the API response
        isSavingRef.current = true;
        setBody(requestParams.CopyBody);
        if (editInModal)
        {
          startEditModalExit();
        }
        setEnableEditBody(false);
        setEditInModal(false);
        setShowBodyIsRequired(false);

        if (conversation.HasPhases)
        {
          requestParams.ConversationPhaseId =
            comboPhaseSelected ?? phaseSelected?.ConversationPhaseId;

          const repPhase = replyPhases.find(x => x.value === requestParams.ConversationPhaseId);
          requestParams.PhaseName = repPhase?.label ?? phaseSelected?.Title;
        }

        ReplyApi.updateReply(api as any, requestParams).then((response: any) =>
        {
          if (isImportant !== reply.IsImportant)
          {
            setTimeout(() =>
            {
              onMarkAsImportant();
            }, 200);
          }

          const newReply = { ...reply };
          if (editInModal)
          {
            newReply.Attachments = requestParams.Attachments;
            newReply.ReplyLayers = requestParams.ReplyLayers;
            setReplyLayers(requestParams.ReplyLayers);
          }
          if (conversation.HasPhases)
          {
            newReply.ConversationPhaseId = requestParams.ConversationPhaseId;
            newReply.PhaseName = requestParams.PhaseName;
          }
          newReply.UpdatedAt = response.UpdatedAt;
          newReply.UpdatedBy = response.UpdatedBy;

          setBody(response.Body);
          setReply(newReply);
          measureSeeMore();

          if (onUpdateReply)
          {
            // Keep isSavingRef locked until initialReply catches up with response.UpdatedAt.
            // The useEffect will clear it once ConversationCardReplies propagates the new reply.
            pendingUpdatedAtRef.current = response.UpdatedAt;
            // The endpoint does not echo layers/attachments back
            onUpdateReply(
              editInModal
                ? {
                  ...response,
                  ReplyLayers: response.ReplyLayers ?? requestParams.ReplyLayers,
                  Attachments: response.Attachments ?? requestParams.Attachments,
                }
                : response,
              reply.ReplyId,
            );
          }
          else
          {
            isSavingRef.current = false;
          }

          ConversationApi.SetConversationsAsViewed(api as any, {
            ConversationIds: String(reply.ConversationId),
          });

          setTimeout(() =>
          {
            highlightReplies();
            updateLinkConversationsByView();
          }, 200);
        });
      }
    },
    [
      bodyEdited,
      reply,
      getTagsInBody,
      SocketConnectionId,
      disableChecklist,
      conversation,
      comboPhaseSelected,
      phaseSelected,
      replyPhases,
      api,
      isImportant,
      measureSeeMore,
      onUpdateReply,
      highlightReplies,
      editInModal,
      editAttachments,
      editLayersSelected,
      startEditModalExit,
    ],
  );

  // Show action menu
  const showActionMenu = useCallback(() =>
  {
    if (!reply.IsEnable) return false;

    const _replyCreatedByID = reply.CreatedById;
    const _personId = CorgeeStorageHelper.personId();
    const _body = '<div>' + reply.Body + '</body>';

    if (!$(_body).find('.detail-update-embed').length)
    {
      if (CorgeeStorageHelper.canDelete() || CorgeeStorageHelper.canEdit())
      {
        return true;
      }

      if (
        !CorgeeStorageHelper.canDelete() &&
        !CorgeeStorageHelper.canEdit() &&
        CorgeeStorageHelper.canTemporary() &&
        _replyCreatedByID === _personId
      )
      {
        const dateNow = new Date();
        const dateReply = new Date(reply.CreatedAt);
        const diffMs = dateNow.getTime() - dateReply.getTime();
        const diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000);
        const diffHrs = Math.round((diffMs % 86400000) / 3600000);
        const diffDays = Math.round(diffMs / 86400000);

        if (diffDays === 0 && diffHrs === 0 && diffMins < 16)
        {
          return true;
        }
      }
    }
    return false;
  }, [reply]);

  // Get click handler for double click
  const getClickHandler = useCallback(
    (onClick: (e: any) => void, onDblClick: (e: any) => void, delay = 250) =>
    {
      let timeoutID: ReturnType<typeof setTimeout> | null = null;
      return function (event: any)
      {
        if (!timeoutID)
        {
          timeoutID = setTimeout(() =>
          {
            onClick(event);
            timeoutID = null;
          }, delay);
        }
        else
        {
          clearTimeout(timeoutID);
          timeoutID = null;
          onDblClick(event);
        }
      };
    },
    [],
  );

  // Update link conversations by view
  const updateLinkConversationsByView = useCallback(() =>
  {
    if (!singleReplyElementRef.current) return;

    const replyContainer = singleReplyElementRef.current.querySelector(
      '.card-content-container-height-listener',
    );
    const linkedConversationsbyReply = replyContainer?.querySelectorAll('a[link-conversation-id]');

    if (linkedConversationsbyReply && linkedConversationsbyReply.length > 0)
    {
      linkedConversationsbyReply.forEach(item =>
      {
        // Skip nodes already handled, this re-runs on every body re-render
        if ((item as HTMLElement).dataset.infoWindowWired === 'true') return;
        (item as HTMLElement).dataset.infoWindowWired = 'true';

        item.classList.add('info-window-linkedItem');
        item.removeAttribute('href');
        item.removeAttribute('target');
        item.addEventListener(
          'click',
          getClickHandler(
            e =>
            {
              e.preventDefault();
              const linkedId = normalizeConversationId(
                item.getAttribute('link-conversation-id'),
              );
              if (linkedId && corgeeFunctions?.openInfoWindow)
              {
                corgeeFunctions.openInfoWindow(linkedId as any);
              }
            },
            e =>
            {
              e.preventDefault();
            },
          ),
        );
      });
    }
  }, [corgeeFunctions, getClickHandler]);

  // Open chat
  const openChat = useCallback(
    (personId?: number) =>
    {
      if (corgeeFunctions?.openChat)
      {
        corgeeFunctions.openChat(personId || reply.CreatedById);
      }
      else
      {
        if (reqNewChat.Participant2 === 1) return;

        if (canOpenChat)
        {
          const objBeforeSearch = JSON.parse(
            localStorage.getItem('C2_BeforeSearchObjectPeople') || '[]',
          );
          const projectIdVal = conversation.ProjectId;
          const UpdateAtVal = conversation.UpdatedAt;
          const ProjectNameVal = conversation.ProjectName;
          const ImageNameVal = reply.ImageName;
          const PeopleIdVal = reply.CreatedById;
          const PeopleNameVal = reply.CreatedBy;

          if (!objBeforeSearch || objBeforeSearch.length === 0)
          {
            const strObjBeforeSearch = JSON.stringify([
              {
                ID: PeopleIdVal,
                Icon: 'icons-menu-assignment',
                ImageName: ImageNameVal,
                IsDashboardsFilter: false,
                LastUpdatedAt: UpdateAtVal,
                Name: PeopleNameVal,
                Order: 1,
                ProjectId: projectIdVal,
                ProjectName: ProjectNameVal,
                Type: PeopleNameVal,
                TypeId: 15,
                isOnLine: false,
              },
            ]);
            localStorage.setItem('C2_BeforeSearchObjectPeople', strObjBeforeSearch);
            localStorage.setItem('C2_IsLastBeforeSearch', '0');
          }
          else
          {
            if (objBeforeSearch.length === 10)
            {
              objBeforeSearch.splice(0, 1);
            }

            const objBeforeSearchNew = objBeforeSearch.filter((e: any) => e.ID !== projectIdVal);
            const ObjBeforeSearch = {
              ID: PeopleIdVal,
              Icon: 'icons-menu-assignment',
              ImageName: ImageNameVal,
              IsDashboardsFilter: false,
              LastUpdatedAt: UpdateAtVal,
              Name: PeopleNameVal,
              Order: 1,
              ProjectId: projectIdVal,
              ProjectName: ProjectNameVal,
              Type: PeopleNameVal,
              TypeId: 15,
              isOnLine: false,
            };
            objBeforeSearchNew.push(ObjBeforeSearch);
            localStorage.setItem('C2_BeforeSearchObjectPeople', JSON.stringify(objBeforeSearchNew));
            localStorage.setItem('C2_IsLastBeforeSearch', '0');
          }

          const isSafari =
            navigator.appVersion.search('Safari') !== -1 &&
            navigator.appVersion.search('Chrome') === -1 &&
            navigator.appVersion.search('CrMo') === -1 &&
            navigator.appVersion.search('CriOS') === -1;

          if (isSafari)
          {
            windowReferenceSafari = window.open();
          }

          ConversationApi.createNewPrivateChat(api as any, reqNewChat).then((chatResponse: any) =>
          {
            const ChatId = chatResponse.ChatID !== undefined ? chatResponse.ChatID : 0;
            const chatWindowUrl = buildChatWindowUrl(ChatId);

            if (isSafari && windowReferenceSafari)
            {
              windowReferenceSafari.location.href = chatWindowUrl;
              return;
            }

            try
            {
              window.open(chatWindowUrl, '_blank');
            }
            catch (e)
            {
              console.error(e);
            }
          });
        }
      }
    },
    [corgeeFunctions, reply, reqNewChat, canOpenChat, conversation, config, api],
  );

  // On hover profile picture
  const onHoverProfilePicture = useCallback((e: React.MouseEvent) =>
  {
    const dm = false; //CorgeeStorageHelper.isDarkMode();
    e.preventDefault();

    // If a host app (e.g. Chat) provides its own profile hover, delegate to it
    // instead of opening the web ProfilePictureModal.
    if (corgeeFunctions?.onHoverProfilePicture)
    {
      const personId = reply?.CreatedById;
      if (personId)
      {
        corgeeFunctions.onHoverProfilePicture(e, personId);
      }
      return;
    }

    const _div = document.getElementsByClassName('corgee_com_profile-picture-modal-outside')[0];
    if (!_div)
    {
      if (!dm)
      {
        $('.see-all-replies').blur();
        $('.corgee_com_card-hide-details-btn').blur();
        $('.corgee_com_card-see-more-btn').blur();
      }

      let positionY = (e.target as HTMLElement).getBoundingClientRect().y;
      let positionX = (e.target as HTMLElement).getBoundingClientRect().x;

      positionY =
        window.innerHeight - positionY < 100 && window.innerHeight - positionY > 70
          ? positionY - 60
          : positionY;
      positionY =
        window.innerHeight - positionY < 70 && window.innerHeight - positionY > 40
          ? positionY - 100
          : positionY;
      positionY =
        window.innerHeight - positionY < 40 && window.innerHeight - positionY > 0
          ? positionY - 120
          : positionY;

      if (window.innerWidth - positionX < 450)
      {
        positionX = positionX - 470;
      }

      setOpenProfilePictureDialog(true);
      setEventHover(e.currentTarget);
      setPositionProfilePictureY(positionY);
      setPositionProfilePictureX(positionX);
    }
  }, [corgeeFunctions, reply]);

  // Close profile picture modal
  const closeProfilePictureModal = useCallback((e?: React.MouseEvent) =>
  {
    if (e) e.preventDefault();
    setOpenProfilePictureDialog(false);
  }, []);

  // On leave miniature picture
  const onLeaveMiniaturePicture = useCallback(() =>
  {
    if (corgeeFunctions?.onLeaveProfilePicture)
    {
      corgeeFunctions.onLeaveProfilePicture();
      return;
    }

    if (profilePictureItemRef.current)
    {
      profilePictureItemRef.current.closeFromOutSide();
    }
  }, [corgeeFunctions]);

  // On hover reactions
  const onHoverReactions = useCallback(() =>
  {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (reactionTriggerRef.current)
    {
      const rect = reactionTriggerRef.current.getBoundingClientRect();
      setSelectorPos({
        right: window.innerWidth - rect.right - 4,
        bottom: window.innerHeight - rect.top + 4,
      });
    }
    setShowSelector(true);
  }, []);

  // Close hover reactions
  const closeHoverReactions = useCallback(() =>
  {
    setShowSelector(false);
  }, []);

  // Start delayed close (allows mouse to move from trigger to selector)
  const startCloseTimer = useCallback(() =>
  {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setShowSelector(false), 150);
  }, []);

  // On select reactions
  const onSelectReactions = useCallback(
    (emoji: string) =>
    {
      const newReply = { ...reply };
      const index = newReply.reactions.findIndex(rp => rp.personid === me);
      const reactions = [...newReply.reactions];

      const reaction: any = {
        ReplyID: newReply.ReplyId,
        ConversationID: newReply.ConversationId,
        PersonID: me,
        Description: name,
        Reaction: emoji,
        reactions: [],
        SocketConnectionId: SocketConnectionId,
        ReplyOwnerID: newReply.CreatedById,
      };

      if (index > -1)
      {
        if (newReply.reactions[index]!.emoji === emoji)
        {
          setShowSelector(false);
          newReply.reactions = reactions.filter(rp => rp.personid !== me);
          reaction.reactions = newReply.reactions;

          ReplyApi.deleteReplyReaction(api as any, reaction);
          if (corgeeFunctions?.onUpdateReplyReactions)
          {
            corgeeFunctions.onUpdateReplyReactions(newReply);
          }
        }
        else
        {
          setShowSelector(false);
          newReply.reactions = reactions.filter(rp => rp.personid !== me);

          newReply.reactions.push({
            ReplyID: newReply.ReplyId,
            conversationid: newReply.ConversationId,
            personid: me,
            by: name ?? '',
            emoji,
            ImageName: imageProfile,
          });

          reaction.reactions = newReply.reactions;

          ReplyApi.updateReplyReaction(api as any, reaction);
          if (corgeeFunctions?.onUpdateReplyReactions)
          {
            corgeeFunctions.onUpdateReplyReactions(newReply);
          }
        }
      }
      else
      {
        setShowSelector(false);
        newReply.reactions.push({
          ReplyID: newReply.ReplyId,
          conversationid: newReply.ConversationId,
          personid: me,
          by: name ?? '',
          emoji,
          ImageName: imageProfile,
        });
        reaction.reactions = newReply.reactions;

        ReplyApi.insertReplyReaction(api as any, reaction);
        if (corgeeFunctions?.onUpdateReplyReactions)
        {
          corgeeFunctions.onUpdateReplyReactions(newReply);
        }
      }

      setReply(newReply);
    },
    [reply, me, name, imageProfile, SocketConnectionId, api, corgeeFunctions],
  );

  // On close modal
  const onCloseModal = useCallback(() =>
  {
    setModal({ open: false, title: '', template: null });
  }, []);

  // Handle change phase summernote
  const handleChangePhaseSumernote = useCallback(
    (event: any) =>
    {
      setComboPhaseSelected(event.value);
      const localPhases = localStorage.getItem(
        CorgeeStorageHelper.appPrefix() + '_ConversationReplyPhaseDefault',
      );
      const parsedPhases = localPhases ? JSON.parse(localPhases) : null;

      if (!parsedPhases)
      {
        const item = [
          {
            ConversationId: conversation.ConversationId,
            ConversationPhaseId: event.value,
          },
        ];
        localStorage.setItem(
          CorgeeStorageHelper.appPrefix() + '_ConversationReplyPhaseDefault',
          JSON.stringify(item),
        );
      }
      else
      {
        const index = parsedPhases.findIndex(
          (p: any) => p.ConversationId === conversation.ConversationId,
        );

        const item = {
          ConversationId: conversation.ConversationId,
          ConversationPhaseId: event.value,
        };

        if (index !== -1)
        {
          parsedPhases.splice(index, 1);
          parsedPhases.push(item);
        }
        else
        {
          parsedPhases.push(item);
        }

        if (parsedPhases.length > 100)
        {
          parsedPhases.splice(0, 1);
        }

        localStorage.setItem(
          CorgeeStorageHelper.appPrefix() + '_ConversationReplyPhaseDefault',
          JSON.stringify(parsedPhases),
        );
      }
    },
    [conversation.ConversationId],
  );

  // On mark as important
  const onMarkAsImportant = useCallback(() =>
  {
    const newImportant = !reply.IsImportant;
    const newReply = { ...reply, IsImportant: newImportant };

    setReply(newReply);
    setIsImportant(newImportant);

    setTimeout(() =>
    {
      if (corgeeFunctions?.onUpdateReply)
      {
        corgeeFunctions.onUpdateReply(conversation, newReply, { IsImportant: newImportant }, 0);
      }
    }, 0);

    const requestParams = {
      ConversationId: conversation.ConversationId,
      ReplyId: newReply.ReplyId,
      IsImportant: newImportant,
      SocketConnectionId: SocketConnectionId ?? '',
    };

    if (corgeeFunctions?.loadingBarStart)
    {
      corgeeFunctions.loadingBarStart();
    }

    ConversationApi.updateReplyImportant(api as any, requestParams)
      .then(() =>
      {
        if (corgeeFunctions?.loadingBarComplete)
        {
          corgeeFunctions.loadingBarComplete();
        }
        ConversationApi.SetConversationsAsViewed(api as any, {
          ConversationIds: String(newReply.ConversationId),
        });
      })
      .catch((error: any) =>
      {
        console.error(error);
      });
  }, [reply, conversation, SocketConnectionId, api, corgeeFunctions]);

  // On update temporal important
  const onUpdateTemporalImportant = useCallback((newImportant: boolean) =>
  {
    setIsImportant(newImportant);
  }, []);

  // Effect for component did mount
  useEffect(() =>
  {
    setTimeout(() =>
    {
      if (singleReplyElementRef.current)
      {
        measureSeeMore();
        countImages(singleReplyElementRef.current);
      }
    });

    // Re-measure on late layout changes: lazy images, web fonts, wide tables
    const content = singleReplyElementRef.current?.querySelector<HTMLElement>(
      '.corgee_com_card-body-description-container > .card-content-container-height-listener > div',
    );
    let observer: ResizeObserver | null = null;
    if (content && typeof ResizeObserver !== 'undefined')
    {
      observer = new ResizeObserver(() => measureSeeMore());
      observer.observe(content);
    }

    // Check if user is able to chat
    if (storageData?.enableChat)
    {
      const newReqNewChat = {
        Participant1: storageData.personid || 0,
        Participant2: reply.CreatedById,
        ChatID: 0,
      };

      if (
        parseInt(String(newReqNewChat.Participant1)) > 0 &&
        parseInt(String(newReqNewChat.Participant2)) > 0 &&
        parseInt(String(newReqNewChat.Participant1)) !==
          parseInt(String(newReqNewChat.Participant2)) &&
        parseInt(String(newReqNewChat.Participant2)) !== 1
      )
      {
        setCanOpenChat(true);
        setReqNewChat(newReqNewChat);
      }
    }

    if (chatData?.enableChat)
    {
      const newReqNewChat = {
        Participant1: chatData.personid || 0,
        Participant2: reply.CreatedById,
        ChatID: 0,
      };

      if (
        parseInt(String(newReqNewChat.Participant1)) > 0 &&
        parseInt(String(newReqNewChat.Participant2)) > 0 &&
        parseInt(String(newReqNewChat.Participant1)) !==
          parseInt(String(newReqNewChat.Participant2)) &&
        parseInt(String(newReqNewChat.Participant2)) !== 1
      )
      {
        setCanOpenChat(true);
        setReqNewChat(newReqNewChat);
      }
    }

    // Linked cards must open in the InfoWindow on every view, ConversationDetails included
    updateLinkConversationsByView();

    return () =>
    {
      observer?.disconnect();
    };
  }, []);

  // Effect for component will receive props
  useEffect(() =>
  {
    // Clear the saving lock once initialReply has been updated with the saved UpdatedAt.
    // This defers the unlock until ConversationCardReplies propagates the new reply data,
    // preventing the effect from reverting optimistic state before the parent catches up.
    if (isSavingRef.current && pendingUpdatedAtRef.current !== null)
    {
      if (initialReply.UpdatedAt === pendingUpdatedAtRef.current)
      {
        isSavingRef.current = false;
        pendingUpdatedAtRef.current = null;
      }
      else
      {
        return;
      }
    }
    else if (isSavingRef.current)
    {
      return;
    }

    if (reply.UpdateReaction)
    {
      const copyReply = { ...reply, UpdateReaction: false };
      setShowReaction(false);
      setReply(copyReply);
      setTimeout(() =>
      {
        setShowReaction(true);
      });
    }

    if (initialReply.IsImportant !== prevIsImportantRef.current)
    {
      prevIsImportantRef.current = initialReply.IsImportant;
      setReply(initialReply);
      setIsImportant(initialReply.IsImportant);
    }

    if (initialReply.Body !== body && !isSavingRef.current)
    {
      if (!initialReply.Body.includes('data:image'))
      {
        setBody(initialReply.Body);
        measureSeeMore();
      }
    }

    if (conversation.Highlight !== highlight)
    {
      setHighlight(conversation.Highlight);
      if (!initialReply.reactSelfUpdate)
      {
        highlightReplies();
      }
    }

    if (repliesPendingToHighlight.length > 0)
    {
      const replyToHighlight = repliesPendingToHighlight.indexOf(reply.ReplyId);
      if (replyToHighlight !== -1 && removePendingHighlight)
      {
        highlightReplies();
        removePendingHighlight(repliesPendingToHighlight[replyToHighlight]!);
      }
    }

    // While the modal is open the color follows the layer selected there
    if (
      initialReply.ReplyLayers &&
      initialReply.ReplyLayers.length > 0 &&
      !(enableEditBody && editInModal)
    )
    {
      const layers = [...initialReply.ReplyLayers].sort((a, b) => (a.PriorityInConfigValue || 0) - (b.PriorityInConfigValue || 0));
      const newFirstLayerColor = layers[0]?.LayerColor ?? layers[0]?.Color;

      if (newFirstLayerColor !== firstLayerColor)
      {
        setFirstLayerColor(newFirstLayerColor);
      }
    }

    if (initialReply.UpdatedAt !== reply.UpdatedAt && !isSavingRef.current)
    {
      setReply(initialReply);
      measureSeeMore();
      if (!initialReply.reactSelfUpdate)
      {
        highlightReplies();
      }
    }

    // Skip while saving: the prop still holds the previous layers
    if (!isSavingRef.current)
    {
      setReplyLayers(initialReply.ReplyLayers);
    }

    setTimeout(() =>
    {
      if (singleReplyElementRef.current)
      {
        countImages(singleReplyElementRef.current);
      }
    });
  }, [
    initialReply,
    conversation,
    repliesPendingToHighlight,
    body,
    highlight,
    isImportant,
    firstLayerColor,
  ]);

  // Computed values
  const isDarkMode = useMemo(() =>
  {
    if (localStorage.getItem('C2_enableDarkMode'))
    {
      return localStorage.getItem('C2_enableDarkMode') === 'true';
    }

    if (
      localStorage.getItem('C2_darkModeChat') &&
      (window.location.href.indexOf('/Chat/') > 0 ||
        window.location.href.indexOf('chat') > 0 ||
        window.location.href.indexOf('Chat') > 0)
    )
    {
      return localStorage.getItem('C2_darkModeChat') === 'true';
    }

    return false;
  }, []);

  const replyToggleContent = useMemo(() =>
  {
    if (!displayFullContent && !enableEditBody)
    {
      return {
        CssClass: 'hide-full-content',
        Text: 'See More',
        classArrow: 'arrow_drop_down',
      };
    }
    return {
      CssClass: 'display-full-content',
      Text: 'See Less',
      classArrow: 'arrow_drop_up',
    };
  }, [displayFullContent, enableEditBody]);

  const isDark = document.documentElement.classList.contains('dark');
  const backgroundColor = isDark ? '#3d3a2a' : '#ffffe1';
  const originalBackgroundColor = isDark ? '#333535' : '#ffffff';

  const htmlBody = useMemo(() =>
  {
    const replyWithInternalBody = { ...reply, Body: body };
    return addTargetBlankToLinks(CorgeeHelper.getBodyValidated(replyWithInternalBody, true));
  }, [reply, body]);

  // Re-rendering the body restores the original href on the linked card anchors
  useEffect(() =>
  {
    updateLinkConversationsByView();
    measureSeeMore();
  }, [htmlBody, enableEditBody, displayFullContent, updateLinkConversationsByView, measureSeeMore]);

  const hoverChatStyle = canOpenChat
    ? 'corgee_com_card-conversation-item-single-reply-open-chat'
    : '';

  const clickOutSideWhiteList = [
    { type: 'class', value: 'selectorFacebook' },
    { type: 'class', value: 'icon-reaction-chat' },
    { type: 'class', value: 'icon-reactions-default' },
  ];

  const reactedByMe = useMemo(() =>
  {
    if (!reply.reactions || reply.reactions.length === 0) return false;
    const names = reply.reactions.map(r => r.by);
    return names.includes(name ?? '');
  }, [reply.reactions, name]);

  const reactedByMeColor = useMemo(() =>
  {
    if (isDarkMode)
    {
      return reactedByMe ? '#ddd' : '#999999';
    }
    return reactedByMe ? '#999999' : '#ddd';
  }, [isDarkMode, reactedByMe]);

  // Memoize expensive DOM parsing operation
  const showSeeMoreContent = useMemo(() =>
  {
    return CorgeeHelper.showSeeMoreContent(reply.Body, reply.CreatedBy);
  }, [reply.Body, reply.CreatedBy]);

  return (
    <div onMouseLeave={closeHoverReactions}>
      <div
        ref={singleReplyElementRef}
        className={`corgee_com_card-conversation-item-single-reply ${
          highlightReply ? 'react-item-highlighted notification-highlight' : ''
        }`}
        style={{
          backgroundColor: highlight ? backgroundColor : originalBackgroundColor,
        }}
      >
        {showActionMenu() && (
          <div className="corgee_com_card-conversation-item-single-reply-action-menu">
            {React.createElement(ActionMenuReply as any, {
              reply,
              conversation,
              api,
              hideModals,
              isImportant: reply.IsImportant,
              corgeeFunctions: { ...corgeeFunctions, editReply: onEditBodyInModal },
              onDeleteReply,
              onAddRepost,
              SocketConnectionId,
              configMenus,
              isInfowindowViewCard,
              onMarkAsImportant,
              onSubmitTime,
              onAddReply,
            })}
          </div>
        )}

        <div className="corgee_com_card-conversation-item-single-reply-team-labels">
          {replyLayers &&
            replyLayers
              .sort((a, b) => a.PriorityInConfigValue - b.PriorityInConfigValue)
              .map(layer => (
                <span
                  key={`rl-${conversationId}-${layer.LayerId}`}
                  className="corgee_com_card-conversation-item-single-reply-team reply-layer-tag"
                  style={{ backgroundColor: layer.LayerColor }}
                ></span>
              ))}
        </div>

        <div
          onClick={() => openChat()}
          onMouseEnter={onHoverProfilePicture}
          onMouseLeave={onLeaveMiniaturePicture}
          className={`corgee_com_card-conversation-item-single-reply-user-picture-image ${hoverChatStyle}`}
          style={{ backgroundImage: `url('${reply.ImageName}')` }}
        ></div>

        <div className="corgee_com_card-conversation-item-single-reply-user-information cancel-drag">
          <div className="corgee_com_card-conversation-item-single-reply-user-name">
            <span className="corgee_com_card-conversation-item-single-reply-author">
              {reply.CreatedBy}
            </span>
            <span
              className="corgee_com_card-conversation-item-single-reply-timestamp enable-help-tooltip"
              data-title={CorgeeHelper.applyFormatDate(
                reply.CreatedAt,
                'MMM[.] DD[,] YYYY [at] hh:mm A',
              )}
            >
              {CorgeeHelper.applyFormatDate(reply.CreatedAt, 'MMM[.] DD[,] YYYY [at] hh:mm A')}
            </span>
            {reply.UpdatedBy && reply.UpdatedBy.length > 0 && (
              <span className="corgee_com_card-conversation-item-single-reply-timestamp updated">
                Edited by: {reply.UpdatedBy + ' - '}
                <span
                  className="corgee_com_card-conversation-item-single-reply-timestamp enable-help-tooltip"
                  data-title={CorgeeHelper.applyFormatDate(
                    reply.UpdatedAt!,
                    'MMM[.] DD[,] YYYY [at] hh:mm A',
                  )}
                >
                  {CorgeeHelper.applyFormatDate(reply.UpdatedAt!, 'MMM[.] DD[,] YYYY [at] hh:mm A')}
                </span>
              </span>
            )}
            {conversation.HasPhases && (
              <span className="corgee_com_card-conversation-item-single-reply-timestamp updated">
                {' - ' + reply.PhaseName}
              </span>
            )}
            {reply.IsImportant && (
              <div className="div-content-reply_important">
                <span className="corgee_reply_important_mark">IMPORTANT</span>
              </div>
            )}
          </div>

          <div className="corgee_com_card-conversation-item-single-reply-user-comment">
            {showSeeMoreContent ? (
              <div
                className={`corgee_com_card-truncated-body-description ${replyToggleContent.CssClass}`}
              >
                <div
                  className="corgee_com_card-body-description-container"
                  onDoubleClick={onEditBody}
                >
                  {(!enableEditBody || editInModal) && (
                    <div className="card-content-container-height-listener">
                      <div
                        dangerouslySetInnerHTML={{ __html: htmlBody }}
                        onClick={handleEmbeddedImagesClick}
                      ></div>
                    </div>
                  )}

                  {enableEditBody && !editInModal && conversation.HasPhases && (
                    <div className="summernote-phases">
                      <Select
                        className="select-summernote-phases"
                        classNamePrefix="reply-phase-select"
                        placeholder="Select a Phase"
                        {...({ clearable: false } as any)}
                        value={replyPhases.find(p => p.value === comboPhaseSelected) ?? null}
                        options={replyPhases as any}
                        {...({ searchable: false } as any)}
                        onChange={handleChangePhaseSumernote}
                      />
                    </div>
                  )}

                  {enableEditBody && !editInModal && (
                    <SummernoteCorgee
                      ref={summernoteRef}
                      selectorContainer=".corgee_com_card-body-description-container"
                      conversationId={conversation.ConversationId}
                      onChangeBody={onChangeSummernote}
                      body={body}
                      layerColor={firstLayerColor}
                      textHeight={150}
                      layers={replyLayers as any}
                      api={api as any}
                      isImportant={reply.IsImportant}
                      isAddReply={true}
                      onUpdateTemporalImportant={onUpdateTemporalImportant}
                      projectId={conversation.ProjectId}
                      configValueId={conversation.ConfigValueId}
                      onKeyDown={onKeyDownEditor}
                    >
                      {showBodyIsRequired && (
                        <div className="corgee_com_validation-message">Enter a valid message.</div>
                      )}
                      <div className="corgee_com_card-publish-options">
                        <div className="corgee_com_card-publish-action">
                          <button
                            className="corgee_com_button corgee_com_button-primary btnChangeColorWithLayer"
                            onClick={onSaveEditBody}
                            style={{
                              backgroundColor: firstLayerColor,
                              borderColor: firstLayerColor,
                            }}
                          >
                            Update
                          </button>
                        </div>
                        <div className="corgee_com_card-publish-action">
                          <button
                            type="button"
                            className="corgee_com_button corgee_com_button-cancel"
                            onClick={onCancelEditBody}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </SummernoteCorgee>
                  )}
                </div>
                {(!enableEditBody || editInModal) && showSeeMoreLink && (
                  <div className="corgee_com_card-see-more-container">
                    <a
                      onClick={onSeeMoreContent}
                      style={{ display: 'inline-flex', alignItems: 'center' }}
                      className="corgee_com_card-see-more-btn"
                    >
                      {replyToggleContent.Text}{' '}
                      <span className="md-font material-icons">
                        {replyToggleContent.classArrow}
                      </span>
                    </a>
                  </div>
                )}
              </div>
            ) : (
              <div className="card-content-container-height-listener">
                <div
                  dangerouslySetInnerHTML={{ __html: htmlBody }}
                  onClick={handleEmbeddedImagesClick}
                ></div>
              </div>
            )}
          </div>

          {reply.Attachments &&
            reply.Attachments.length > 0 &&
            React.createElement(AttachmentsContent as any, {
              item: conversation,
              isSingleReply: 'true',
              attachments: reply.Attachments,
              corgeeFunctions,
              reactSelfUpdate: false,
              config,
              api,
            })}

          {reply.reactions && (
            <ClickOutSide
              onClickOutSide={closeHoverReactions}
              whiteList={clickOutSideWhiteList as any}
            >
              <span
                ref={reactionTriggerRef}
                onMouseEnter={onHoverReactions}
                onMouseLeave={startCloseTimer}
                className="single-reply icon-reactions-default"
                style={{
                  position: 'relative',
                  float: 'right',
                  ...(isHomeView ? { marginRight: '6px' } : { right: '-6px' }),
                  top: showSeeMoreLink ? '0px' : '4px',
                  color: reactedByMeColor,
                }}
              >
                thumb_up_off
              </span>
            </ClickOutSide>
          )}

          <div className="h-5">
            {reply.reactions && reply.reactions.length > 0 && (
              <div
                className="selectorFacebook"
                style={{
                  position: 'relative',
                  float: 'right',
                  zIndex: 0,
                  marginRight: '4px',
                  bottom: '-28px',
                  top: showSeeMoreLink ? '-1px' : '3px',
                  height: 20,
                }}
              >
                {React.createElement(FacebookCounter as any, {
                  id: String(reply.ReplyId),
                  counters: reply.reactions,
                  bg: isDarkMode ? '#2d2d2f' : '#fff',
                  onclick: onHoverReactions,
                })}
              </div>
            )}
          </div>
        </div>

        <ImagesGallery
          open={openGallery}
          currentImageId={selectedImageId}
          attachments={embeddedImages.map(
            (img): GalleryAttachment => ({
              AttachmentId: img.AttachmentId || 0,
              EmbeddedImageId: img.EmbeddedImageId,
              FileUrl: img.FileUrl || '',
              Thumbnail: { FileUrl: img.Thumbnail?.FileUrl || '' },
              FileName: img.FileName || '',
              FileExtension: img.FileExtension || '',
              Favorite: img.Favorite,
              Description: img.Description,
              Title: img.Title,
              // Add conversation context for default reply target
              ConversationId: conversation.ConversationId,
              ConversationTitle: (conversation as any).Title,
              ProjectName: (conversation as any).ProjectName || '',
              ProjectId: conversation.ProjectId,
            }),
          )}
          isEmbedded={true}
          onClose={onCloseGallery}
        />

        {React.createElement(ProfilePictureModal as any, {
          ref: profilePictureItemRef,
          closeProfilePictureModal,
          openProfilePictureDialog,
          config,
          api,
          reply,
          eventHover,
          positionY: positionProfilePictureY,
          positionX: positionProfilePictureX,
          canOpenChat: true,
          canOpenCard: true,
          openNewWindow,
          openChat,
        })}

        <CorgeeModalStats
          className=""
          openModal={modal.open}
          title={modal.title}
          onClose={onCloseModal}
          isDraggable={true}
          closeOnEscape={false}
          hasMaxHeight={true}
          width={350}
          height={120}
        >
          <div>{modal.template && modal.template.map(element => element)}</div>
        </CorgeeModalStats>
      </div>

      {showSelector &&
        ReactDOM.createPortal(
          <div
            className="selectorFacebook"
            onMouseEnter={() => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }}
            onMouseLeave={startCloseTimer}
            style={{
              position: 'fixed',
              right: selectorPos.right,
              bottom: selectorPos.bottom,
              zIndex: 9999,
            }}
          >
            <FacebookSelector
              reactions={['like', 'love', 'haha', 'wow', 'sad', 'angry', 'eyes']}
              bg={isDarkMode ? '#2d2d2f' : '#fff'}
              iconsize={38}
              onselect={onSelectReactions}
            />
          </div>,
          document.body,
        )}

      {/* Edit Reply modal. Portaled to body: dnd-kit transforms clamp fixed positioning */}
      {((enableEditBody && editInModal) || editModalExiting) && ReactDOM.createPortal(
        <div className="edit-reply-modal-portal">
          <div className="custom-modal display-block">
            <div
              ref={editReplyModalRef}
              style={editReplyDragPos ? {
                position: 'fixed',
                left: `${editReplyDragPos.x}px`,
                top: `${editReplyDragPos.y}px`,
              } : {
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <md-dialog
                className={`md-transition-in edit-reply-modal${editReplyDragPos ? ' edit-reply-modal-ready' : ''}${editModalExiting ? ' edit-reply-modal-closing' : ''}`}
                role="dialog"
                style={{ position: 'static' }}
                id={`editReplyModal-${reply.ReplyId}`}
              >
                <md-toolbar onMouseDown={handleEditReplyDragMouseDown}>
                  <div className="md-toolbar-tools">
                    <div className="popup-header-title">Edit Reply</div>
                  </div>
                  <div
                    className="close-control modal-icon-close-pos"
                    role="button"
                    onClick={onCancelEditBody}
                  >
                    <i className="icon-incorrect-icon"></i>
                  </div>
                </md-toolbar>
                <md-dialog-content>
                  <div className="md-dialog-content edit-reply-modal-content">
                    <div className="corgee_com_card-body-description-container">
                      {conversation.HasPhases && (
                        <div className="summernote-phases">
                          <Select
                            className="select-summernote-phases"
                            classNamePrefix="reply-phase-select"
                            placeholder="Select a Phase"
                            {...({ clearable: false } as any)}
                            value={replyPhases.find(p => p.value === comboPhaseSelected) ?? null}
                            options={replyPhases as any}
                            {...({ searchable: false } as any)}
                            onChange={handleChangePhaseSumernote}
                          />
                        </div>
                      )}

                      {((enableEditBody && editInModal) || editModalExiting) && (
                        <SummernoteCorgee
                          ref={(instance: SummernoteCorgeeRef | null) =>
                          {
                            summernoteEditModalRef.current = instance;
                            summernoteRef.current = instance;
                          }}
                          selectorContainer=".corgee_com_card-body-description-container"
                          conversationId={conversation.ConversationId}
                          onChangeBody={onChangeSummernote}
                          body={body}
                          layerColor={firstLayerColor}
                          textHeight={150}
                          layers={editLayersSelected as any}
                          api={api as any}
                          isImportant={reply.IsImportant}
                          isAddReply={true}
                          onUpdateTemporalImportant={onUpdateTemporalImportant}
                          projectId={conversation.ProjectId}
                          configValueId={conversation.ConfigValueId}
                          onKeyDown={onKeyDownEditor}
                        >
                          {isUploadingFile ? (
                            <div className="corgee_com_validation-message">
                        Uploading file, please wait.
                            </div>
                          ) : showBodyIsRequired ? (
                            <div className="corgee_com_validation-message">Enter a valid message.</div>
                          ) : null}

                          <div
                            className="corgee_com_card-add-replay-uploader edit-reply-modal-uploader"
                            style={{ borderColor: firstLayerColor }}
                          >
                            {React.createElement(FileUpload as any, {
                            // Read on mount only; the modal remounts on every open
                              initialFiles: initialAttachmentFiles,
                              onChangeFileUpload: onChangeFileUpload,
                              onRemoveAttachment: onRemoveAttachment,
                              config: api,
                            })}
                          </div>

                          <div
                            className="corgee_com_card-publish-layers edit-reply-modal-layers"
                            style={{ position: 'relative', zIndex: 1000 }}
                          >
                            <div className="corgee_com_card-item-security-settings-container">
                              <div className="corgee_com_card-item-security-settings-label">Layers</div>

                              <div
                                className="corgee_com_card-layers-container-widget"
                                style={{ borderColor: firstLayerColor }}
                                onMouseDownCapture={() => { layersTouchedRef.current = true; }}
                                onKeyDownCapture={() => { layersTouchedRef.current = true; }}
                              >
                                {React.createElement(CardConversationLayers as any, {
                                  ref: layersWidgetRef,
                                  apiConfig: api,
                                  conversation,
                                  isNew: true,
                                  disableAutoSave: false,
                                  onChangedLayers: onChangeEditLayers,
                                  isInlineWidget: true,
                                  closeMenuOnSelect: true,
                                  restrictToConversationLayers: true,
                                  addLayerToCard: onAddLayerToCard,
                                  addLayerToProject: onAddLayerToProject,
                                  onSetFunctionsCardLayers: onSetFunctionsCardLayers,
                                })}
                              </div>
                            </div>
                          </div>

                          {showLayerIsRequired && (
                            <div className="corgee_com_validation-message">Select a layer.</div>
                          )}

                          <div className="corgee_com_card-publish-options edit-reply-modal-actions">
                            <div className="corgee_com_card-publish-action">
                              <button
                                type="button"
                                className="corgee_com_button corgee_com_button-cancel"
                                onClick={onCancelEditBody}
                              >
                          Cancel
                              </button>
                            </div>
                            <div className="corgee_com_card-publish-action">
                              <button
                                type="button"
                                className="corgee_com_button corgee_com_button-primary btnChangeColorWithLayer"
                                onClick={onSaveEditBody}
                                style={{
                                  backgroundColor: firstLayerColor,
                                  borderColor: firstLayerColor,
                                }}
                                disabled={isUploadingFile || editLayersSelected.length === 0}
                              >
                          Update
                              </button>
                            </div>
                          </div>
                        </SummernoteCorgee>
                      )}
                    </div>
                  </div>
                </md-dialog-content>
              </md-dialog>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Portaled for the same reason as the modal above */}
      {showConfirmationModal && ReactDOM.createPortal(
        <div className="single-reply-draft-modal-portal">
          <CustomModal show={true}>
            <md-dialog
              className="md-transition-in"
              role="dialog"
              style={{ opacity: '1', position: 'static' }}
              id="draftConfirmationModal"
            >
              <form>
                <md-toolbar style={{ cursor: 'default' }}>
                  <div className="md-toolbar-tools">
                    <div className="popup-header-title">Confirm Action</div>
                  </div>
                  <div
                    className="close-control modal-icon-close-pos"
                    role="button"
                    onClick={() => closeEditor(false)}
                  >
                    <i className="icon-incorrect-icon"></i>
                  </div>
                </md-toolbar>
                <md-dialog-content style={{ display: 'table' }} id="dialog_23">
                  <div className="md-dialog-content delete-item-modal">
                Are you sure you want to delete? Draft will be missed.
                  </div>
                </md-dialog-content>

                <md-dialog-actions>
                  <span className="flex"></span>
                  <button
                    className="modal-window-button confirm-cancel md-button md-ink-ripple"
                    type="button"
                    onClick={() => closeEditor(false)}
                  >
                    <span>Cancel</span>
                  </button>
                  <button
                    className="modal-window-button confirm-continue md-button md-ink-ripple"
                    type="button"
                    onClick={() => closeEditor(true)}
                  >
                    <span>Confirm</span>
                  </button>
                </md-dialog-actions>
              </form>
            </md-dialog>
          </CustomModal>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default SingleReply;
